import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Icon } from '@components/icons';
import { usePrefersReducedMotion } from '@hooks';

const StyledPublicationsSection = styled.section`
  max-width: 1000px;

  .publications-list {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StyledPublicationItem = styled.li`
  position: relative;
  padding: 28px;
  border: 1px solid var(--lightest-navy);
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(23, 192, 235, 0.05) 0%, rgba(255, 0, 127, 0.02) 100%);
  transition: all var(--transition);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--green), #00d4ff);
    opacity: 0;
    transition: opacity var(--transition);
  }

  &:hover {
    border-color: var(--green);
    box-shadow: 0 10px 30px -15px rgba(2, 255, 150, 0.2);
    transform: translateY(-5px);
    background: linear-gradient(135deg, rgba(23, 192, 235, 0.1) 0%, rgba(255, 0, 127, 0.05) 100%);

    &::before {
      opacity: 1;
    }

    .pub-title a {
      color: var(--green);
    }
  }

  .pub-title {
    font-size: var(--fz-lg);
    font-weight: 700;
    margin: 0 0 12px 0;
    line-height: 1.4;
    color: var(--lightest-slate);

    a {
      color: var(--lightest-slate);
      text-decoration: none;
      transition: color var(--transition);

      &:hover {
        color: var(--green);
      }
    }
  }

  .pub-meta {
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    color: var(--slate);
    margin-bottom: 16px;
    line-height: 1.6;
    flex-wrap: wrap;
    display: flex;
    gap: 8px;
    align-items: center;

    .meta-badge {
      display: inline-block;
      padding: 4px 10px;
      background: rgba(2, 255, 150, 0.1);
      border: 1px solid rgba(2, 255, 150, 0.3);
      border-radius: 4px;
      color: var(--green);
      font-size: var(--fz-xxs);
      white-space: nowrap;
    }
  }

  .pub-description {
    font-size: var(--fz-sm);
    color: var(--light-slate);
    line-height: 1.65;
    margin-bottom: 16px;
    flex-grow: 1;

    p {
      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .pub-links {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: auto;
    padding-top: 16px;
    border-top: 1px solid var(--lightest-navy);
  }

  .pub-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--slate);
    text-decoration: none;
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    transition: all var(--transition);
    padding: 6px 12px;
    border-radius: 4px;
    background: rgba(2, 255, 150, 0.08);
    border: 1px solid rgba(2, 255, 150, 0.2);

    &:hover {
      color: var(--green);
      background: rgba(2, 255, 150, 0.15);
      border-color: var(--green);
      transform: translateX(2px);
    }

    svg {
      width: 14px;
      height: 14px;
      opacity: 0.9;
    }
  }
`;

const Publications = () => {
  const data = useStaticQuery(graphql`
    query {
      publications: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/publication/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              authors
              venue
              location
              presentation
              year
              url
              github
            }
            html
          }
        }
      }
    }
  `);

  const revealContainer = useRef(null);
  const revealItems = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return;
    sr.reveal(revealContainer.current, srConfig());
    revealItems.current.forEach((el, i) => el && sr.reveal(el, srConfig(i * 80)));
  }, []);

  const publications = data.publications.edges;

  return (
    <StyledPublicationsSection id="publications" ref={revealContainer}>
      <h2 className="numbered-heading">Publications</h2>

      <ul className="publications-list">
        {publications &&
          publications.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const {
              title,
              authors,
              venue,
              location: pubLocation,
              presentation,
              year,
              url,
              github,
            } = frontmatter;

            return (
              <StyledPublicationItem
                key={i}
                ref={el => (revealItems.current[i] = el)}>
                <h3 className="pub-title">
                  <a href={url} target="_blank" rel="noreferrer">
                    {title}
                  </a>
                </h3>
                <div className="pub-meta">
                  {authors && (() => {
                    const parts = authors.split(',').map(s => s.trim());
                    return (
                      <span>
                        <strong>{parts[0]}</strong>
                        {parts.length > 1 && `, ${parts.slice(1).join(', ')}`}
                      </span>
                    );
                  })()}
                  {year && <span className="meta-badge">{year}</span>}
                  {venue && <span className="meta-badge">{venue}</span>}
                  {pubLocation && <span className="meta-badge">{pubLocation}</span>}
                  {presentation && <span className="meta-badge">{presentation}</span>}
                </div>
                {html && (
                  <div
                    className="pub-description"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                )}
                <div className="pub-links">
                  {url && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="pub-link"
                      aria-label={`View preprint: ${title}`}>
                      <Icon name="External" />
                      Preprint
                    </a>
                  )}
                  {github && (
                    <a
                      href={github}
                      target="_blank"
                      rel="noreferrer"
                      className="pub-link"
                      aria-label={`View code: ${title}`}>
                      <Icon name="GitHub" />
                      Code
                    </a>
                  )}
                </div>
              </StyledPublicationItem>
            );
          })}
      </ul>
    </StyledPublicationsSection>
  );
};

export default Publications;
